/**
 * DownloadApp — pagina pubblica per suggerire download PWA e install nativa iOS.
 * Mostra QR Expo Go per test rapido, badge TestFlight quando disponibile, install PWA.
 */
import { useEffect, useState } from "react";
import { Download, Smartphone, Globe, Apple, QrCode as QrCodeIcon, ShieldCheck, Zap } from "lucide-react";

// Preso da /app/mobile — se cambia, aggiorna qui.
const EXPO_PROJECT_URL = "exp://exp.host/@primevision/agenda-push";
const TESTFLIGHT_URL = null; // sostituisci con link TestFlight quando disponibile

// Generatore QR gratuito lato client (via API pubblica goqr.me — nessuna dipendenza npm)
const qrSrc = (data) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=0&data=${encodeURIComponent(data)}`;

export default function DownloadApp() {
  const [pwaEvent, setPwaEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    // Detect standalone (iOS)
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setInstalled(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const doInstallPwa = async () => {
    if (!pwaEvent) return;
    pwaEvent.prompt();
    await pwaEvent.userChoice;
    setPwaEvent(null);
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }} data-testid="download-app-page">
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <header className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-70">
            <Download size={14}/> Scarica Agenda Push
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">
            La tua Agenda Push, ovunque
          </h1>
          <p className="text-sm opacity-80 max-w-2xl mx-auto">
            Web PWA installabile, iPhone (in preparazione), Android in arrivo. Sotto trovi le
            opzioni disponibili oggi.
          </p>
        </header>

        {/* iOS section */}
        <section className="pxos-card p-6 space-y-4" data-testid="download-ios-section">
          <div className="flex items-center gap-2">
            <Apple size={22}/>
            <h2 className="font-display text-xl font-semibold">iPhone (iOS)</h2>
          </div>

          {TESTFLIGHT_URL ? (
            <div className="rounded-lg p-4 space-y-2" style={{ background: "#E6F5EC" }}>
              <div className="text-sm font-semibold" style={{ color: "#1F6B3A" }}>
                Beta pubblica su TestFlight disponibile 🎉
              </div>
              <p className="text-sm">
                Installa TestFlight dallo store Apple, poi apri questo link dal tuo iPhone:
              </p>
              <a href={TESTFLIGHT_URL} className="pxos-btn-primary text-sm inline-flex items-center gap-2"
                 data-testid="download-testflight-link" target="_blank" rel="noreferrer">
                <Apple size={14}/> Apri in TestFlight
              </a>
            </div>
          ) : (
            <div className="rounded-lg p-4 space-y-3" style={{ background: "#FFF6E0" }}>
              <div className="text-sm font-semibold" style={{ color: "#8A5A00" }}>
                App Store: in preparazione
              </div>
              <p className="text-sm">
                L'app nativa iOS è in fase finale di test. Nel frattempo puoi:
              </p>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li>Usare la <b>PWA installabile</b> qui sotto (funziona come app, con icona in home)</li>
                <li>Provare la <b>build di sviluppo via Expo Go</b> (scansiona il QR sotto)</li>
              </ul>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="text-center space-y-2">
                <img src={qrSrc(EXPO_PROJECT_URL)} alt="QR Expo Go"
                     width={160} height={160}
                     className="rounded-lg border" data-testid="expo-qr"/>
                <div className="text-xs opacity-70 flex items-center justify-center gap-1">
                  <QrCodeIcon size={11}/> QR per Expo Go
                </div>
              </div>
              <div className="flex-1 text-sm space-y-2">
                <div className="font-semibold">Come usare Expo Go:</div>
                <ol className="list-decimal pl-4 space-y-1 opacity-90">
                  <li>Installa <b>Expo Go</b> da App Store</li>
                  <li>Apri Expo Go → tab "Scan"</li>
                  <li>Inquadra il QR qui accanto</li>
                  <li>L'app si apre in modalità sviluppo</li>
                </ol>
                <div className="text-xs opacity-60 pt-1">
                  Nota: Expo Go è per test. La versione definitiva su App Store avrà icona propria e sarà più veloce.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PWA section */}
        <section className="pxos-card p-6 space-y-4" data-testid="download-pwa-section">
          <div className="flex items-center gap-2">
            <Globe size={22}/>
            <h2 className="font-display text-xl font-semibold">Web PWA (Progressive Web App)</h2>
          </div>
          <p className="text-sm opacity-80">
            L'app funziona nel tuo browser e può essere installata come app nativa in un click.
            Funziona su iPhone, Android, Mac, Windows.
          </p>

          {installed ? (
            <div className="rounded-lg p-4" style={{ background: "#E6F5EC" }}>
              <div className="text-sm font-semibold" style={{ color: "#1F6B3A" }}>
                ✓ PWA già installata su questo dispositivo
              </div>
            </div>
          ) : pwaEvent ? (
            <button onClick={doInstallPwa}
                    className="pxos-btn-primary text-sm inline-flex items-center gap-2"
                    data-testid="pwa-install-btn">
              <Download size={14}/> Installa come app
            </button>
          ) : (
            <div className="rounded-lg p-4 space-y-2" style={{ background: "#F3F4F6" }}>
              <div className="text-sm font-semibold">Come installare manualmente</div>
              <div className="text-sm">
                <b>Safari iPhone:</b> tocca l'icona <span className="inline-block px-1.5 py-0.5 border rounded">Condividi</span> in basso → <b>Aggiungi a schermata Home</b>.
              </div>
              <div className="text-sm">
                <b>Chrome Android:</b> menu ⋮ → <b>Installa app</b>.
              </div>
              <div className="text-sm">
                <b>Desktop:</b> nella barra indirizzi, clicca l'icona <b>+</b> a destra dell'URL.
              </div>
            </div>
          )}
        </section>

        {/* Android section */}
        <section className="pxos-card p-6 space-y-3" data-testid="download-android-section">
          <div className="flex items-center gap-2">
            <Smartphone size={22}/>
            <h2 className="font-display text-xl font-semibold">Android</h2>
          </div>
          <div className="rounded-lg p-4" style={{ background: "#FFF6E0" }}>
            <div className="text-sm font-semibold" style={{ color: "#8A5A00" }}>
              Play Store: in valutazione
            </div>
            <p className="text-sm mt-2">
              Priorità corrente: iOS. Il rilascio Play Store è previsto dopo il rodaggio della versione iPhone.
              Nel frattempo puoi installare la PWA da Chrome (istruzioni sopra).
            </p>
          </div>
        </section>

        {/* Trust footer */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center pt-4">
          <div className="pxos-card p-4 space-y-1">
            <ShieldCheck size={22} className="mx-auto opacity-70"/>
            <div className="text-sm font-semibold">Privacy prima di tutto</div>
            <div className="text-xs opacity-70">Row-Level Security · GDPR</div>
          </div>
          <div className="pxos-card p-4 space-y-1">
            <Zap size={22} className="mx-auto opacity-70"/>
            <div className="text-sm font-semibold">Sync in tempo reale</div>
            <div className="text-xs opacity-70">Google Calendar bidirezionale</div>
          </div>
          <div className="pxos-card p-4 space-y-1">
            <Apple size={22} className="mx-auto opacity-70"/>
            <div className="text-sm font-semibold">Nativa iOS</div>
            <div className="text-xs opacity-70">Face ID · Notifiche · Deep link</div>
          </div>
        </section>

        <footer className="text-center text-xs opacity-60 pt-6">
          Supporto:{" "}
          <a href="mailto:info@primevision.it" className="underline" data-testid="download-support-link">
            info@primevision.it
          </a>
        </footer>
      </div>
    </div>
  );
}
